import { apiClient } from '@kss/api';

/**
 * Auto-incrementing business code generator (e.g. SP-00001, CUS-00002).
 *
 * The user wants the Code field on every form to:
 *   - prefill with the NEXT code (max existing numeric suffix + 1) when
 *     a new record is opened,
 *   - keep a fixed PREFIX per entity (only the prefix is configurable),
 *   - be read-only for non-admins (admins may override).
 *
 * Strategy: fetch the resource list once, find the highest numeric tail
 * across rows whose code starts with `<prefix>-`, and return
 * `<prefix>-<n+1>` zero-padded to 5 digits. Falls back to `<prefix>-00001`
 * when the list is empty or unreachable.
 *
 * Per-entity prefixes can be overridden in CODE_PREFIXES; otherwise the
 * caller passes a derived prefix.
 */

/** Canonical prefixes the user expects, keyed by registry entity name. */
export const CODE_PREFIXES: Record<string, string> = {
  Supplier: 'SP',
  Customer: 'CUST',
  Product: 'PRD',
  ProductCategory: 'PC',
  ProductBrand: 'BR',
  Employee: 'EMP',
  Warehouse: 'WH',
  Branch: 'BR',
  SalesInvoice: 'SI',
  SalesOrder: 'SO',
  PurchaseInvoice: 'PI',
  PurchaseOrder: 'PO',
  CreditNote: 'CN',
  DebitNote: 'DN',
  DeliveryChallan: 'DC',
  SalesReturn: 'SR',
};

function pickList(d: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(d)) return d as Array<Record<string, unknown>>;
  if (d && typeof d === 'object') {
    const o = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
    if (Array.isArray(o.items)) return o.items as Array<Record<string, unknown>>;
    if (Array.isArray(o.data)) return o.data as Array<Record<string, unknown>>;
    if (o.data && Array.isArray((o.data as { items?: unknown[] }).items)) {
      return (o.data as { items: Array<Record<string, unknown>> }).items;
    }
  }
  return [];
}

const PAD = 5;

/**
 * Compute the next code for a resource given its API base path + prefix.
 * Returns e.g. "SP-00007".
 */
export async function nextCode(basePath: string, prefix: string): Promise<string> {
  const p = prefix.replace(/[^A-Za-z0-9]/g, '').toUpperCase() || 'CODE';
  let max = 0;
  try {
    const r = await apiClient.get(basePath);
    const rows = pickList(r.data);
    const re = new RegExp(`^${p}-?(\\d+)$`, 'i');
    for (const row of rows) {
      const code = String(row.code ?? '');
      const m = re.exec(code);
      if (m) {
        const n = parseInt(m[1]!, 10);
        if (Number.isFinite(n) && n > max) max = n;
      }
    }
  } catch {
    /* unreachable list → start at 1 */
  }
  return `${p}-${String(max + 1).padStart(PAD, '0')}`;
}

/**
 * Synchronous fallback used before the async `nextCode` list resolves.
 *
 * Must be UNIQUE: if the user saves before `nextCode` replaces it (or if
 * that fetch errors), a fixed `PREFIX-00001` placeholder collides with any
 * other record saved the same way → HTTP 409 duplicate code. A base-36
 * timestamp suffix keeps every placeholder distinct while `nextCode` still
 * refines it to a clean sequential code when it resolves in time.
 */
export function provisionalCode(prefix: string): string {
  const p = prefix.replace(/[^A-Za-z0-9]/g, '').toUpperCase() || 'CODE';
  return `${p}-${Date.now().toString(36).toUpperCase().slice(-PAD)}`;
}
